import clsx from "clsx";
import { FC, useEffect } from "react";

import { AIChatPanel } from "./ai/components/AIChatPanel";
import { MOBILE_OVERLAY_ROUTE_QUERY_PARAM, MobileOverlayRouteType } from "./constants";
import { ClassChatSidebarPanel } from "./materials/section/ClassChatSidebarPanel";
import { SectionUsersSidebarPanel } from "./materials/section/SectionUsersSidebarPanel";
import { ManageSectionPagesMobilePanel } from "./materials/section/content/pages/ManageSectionPagesMobilePanel";
import { PlannerSidebarPanel } from "./materials/section/planner/PlannerSidebarPanel";
import { InstructorNotesSidebarPanel } from "./materials/sidebar/InstructorNotesSidebarPanel";
import { UserProfileSidebarPanel } from "./materials/sidebar/UserProfileSidebarPanel";
import {
  shouldCloseMobileOnlyOverlay,
  shouldUseMobileRoutingOverlay,
} from "./responsive-overlay-routing";

import { MobileMainMenu } from "components/menus/MobileMainMenu";
import { useIsLgOrLarger, useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useAv } from "utils/av";
import { useQueryParam, useQueryParamsActions } from "utils/routing";

const MOBILE_OVERLAY_ROUTE_COMPONENTS: Record<MobileOverlayRouteType, FC> = {
  menu: MobileMainMenu,
  users: SectionUsersSidebarPanel,
  profile: UserProfileSidebarPanel,
  instructor_notes: InstructorNotesSidebarPanel,
  planner: PlannerSidebarPanel,
  ai: AIChatPanel,
  chat: ClassChatSidebarPanel,
  manage_pages: ManageSectionPagesMobilePanel,
};

/**
 * Renders supported `overlay=` panels over the page below lg.
 * Shared panels render in PageSecondarySidebar at lg and above.
 * This component must be inside a relative container so the panel stays over the page.
 */
export const MobileRoutingOverlay = () => {
  const { deleteQueryParams } = useQueryParamsActions();
  const isMdOrLarger = useIsMdOrLarger();
  const isLgOrLarger = useIsLgOrLarger();

  const { isInVirtualClassOrObserving } = useAv();

  const selectedOverlayRoute = useQueryParam(
    MOBILE_OVERLAY_ROUTE_QUERY_PARAM,
  ) as MobileOverlayRouteType | null;

  useEffect(() => {
    if (shouldCloseMobileOnlyOverlay(selectedOverlayRoute, isMdOrLarger)) {
      deleteQueryParams([MOBILE_OVERLAY_ROUTE_QUERY_PARAM]);
    }
  }, [isMdOrLarger, selectedOverlayRoute, deleteQueryParams]);

  if (!shouldUseMobileRoutingOverlay(selectedOverlayRoute, isLgOrLarger)) return null;

  const isMobileMainMenuOpen = selectedOverlayRoute === "menu";
  const MobileOverlayComponent = MOBILE_OVERLAY_ROUTE_COMPONENTS[selectedOverlayRoute];

  // NOTE: the mobile menu is a special case because its height extends to the top and bottom of the screen,
  // covering the navbar and footer, but its width is fixed to 340px so the underlying content is still visible
  // through the modal backdrop.
  return (
    <>
      {isMobileMainMenuOpen && (
        <div
          className="modal-backdrop"
          onClick={() => deleteQueryParams([MOBILE_OVERLAY_ROUTE_QUERY_PARAM])}
        />
      )}
      <div
        className={clsx("absolute z-[100] bg-white data-[dark]:bg-black-tint-15", {
          "page content-sidebar-panel inset-0 bottom-0 flex h-full w-full flex-col overflow-hidden border-r-2 border-black-tint-90 bg-black-tint-95 px-3 py-[10px]":
            !isMobileMainMenuOpen,
          "-top-[calc(var(--navbar-height)+var(--participant-bar-height))] right-0 h-svh w-[340px]":
            isMobileMainMenuOpen,
          "!py-0": isInVirtualClassOrObserving,
        })}
        data-dark={isInVirtualClassOrObserving || undefined}
      >
        <MobileOverlayComponent />
      </div>
    </>
  );
};
